import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import GossipCard, { formatHeat, statusClass } from "@/components/base/GossipCard";
import { gossipList } from "@/mocks/gossipList";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const item = gossipList.find((g) => g.id === id);

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 w-full px-4 md:px-8 py-20 flex flex-col items-center justify-center text-center">
          <span className="w-16 h-16 mb-5 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="font-heading font-black text-2xl text-foreground-950 mb-3">这条盘点不存在或已下线</h1>
          <p className="text-sm text-foreground-600 mb-6 max-w-md">
            可能链接已经过期，也可能是条目正在重新整理。回到完整榜单重新挑一条吧。
          </p>
          <Link
            to="/rankings"
            className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回 30 条大盘点
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = [
    ...gossipList.filter((g) => g.category === item.category && g.id !== item.id),
    ...gossipList.filter((g) => g.category !== item.category && g.id !== item.id),
  ].slice(0, 3);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: item.title,
      description: item.summary,
      articleSection: item.category,
      datePublished: item.date,
      dateModified: item.date,
      inLanguage: "zh-CN",
      url: `${origin}/topic/${item.id}`,
      author: { "@type": "Organization", name: "瓜网30" },
      publisher: { "@type": "Organization", name: "瓜网30", url: `${origin}/` },
      keywords: item.tags.join(","),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "30条大盘点", item: `${origin}/rankings` },
        { "@type": "ListItem", position: 3, name: item.title, item: `${origin}/topic/${item.id}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title={`${item.title}｜瓜网30大盘点`}
        description={`${item.summary} 本条属于「${item.category}」，当前状态：${item.status}，涉及核心人物 ${item.people.join("、")}，附完整事件脉络时间线。`}
        keywords={`${item.tags.join(",")},${item.category},瓜网30,${item.people.join(",")}`}
        canonicalPath={`/topic/${item.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="flex-1 w-full">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-6 md:py-8">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "30条大盘点", to: "/rankings" },
                { label: `NO.${item.rank} ${item.category}` },
              ]}
            />
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-10" aria-labelledby="topic-title">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                  NO.{item.rank}
                </span>
                <Link
                  to={`/rankings?category=${encodeURIComponent(item.category)}`}
                  className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 border border-accent-200 hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {item.category}
                </Link>
                <span className={`text-xs px-2.5 py-1 rounded-full border ${statusClass(item.status)}`}>
                  {item.status}
                </span>
                <span className="text-xs text-foreground-500">{item.date}</span>
              </div>

              <article>
                <h1 id="topic-title" className="font-heading font-black text-2xl md:text-4xl leading-snug text-foreground-950">
                  {item.title}
                </h1>

                <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed border-l-4 border-accent-400 pl-4">
                  {item.summary}
                </p>

                <div className="mt-6 w-full h-56 md:h-80 rounded-lg overflow-hidden">
                  <img
                    src={item.cover}
                    alt={`${item.title} - 瓜网30事件脉络配图`}
                    title={`${item.title} - 瓜网30`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="mt-8">
                  <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950 mb-3">
                    这条瓜为什么值得看
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-loose">{item.detail}</p>
                </div>

                <div className="mt-10">
                  <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950 mb-6">
                    事件脉络
                  </h2>
                  <ol className="relative border-l-2 border-background-300 ml-3 space-y-7 pl-6 md:pl-8">
                    {item.timeline.map((step, index) => (
                      <li key={`${step.date}-${index}`} className="relative">
                        <span className="absolute -left-[35px] md:-left-[43px] top-0 w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading font-bold text-xs">
                          {index + 1}
                        </span>
                        <p className="font-heading font-bold text-sm md:text-base text-primary-700 mb-1.5">
                          {step.date}
                        </p>
                        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{step.text}</p>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500 mr-1">相关标签</span>
                  {item.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-background-100 border border-background-200 text-foreground-700">
                      #{tag}
                    </span>
                  ))}
                </div>
              </article>
            </div>

            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 space-y-5">
                <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                  <h2 className="font-heading font-bold text-base text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-file-list-3-line text-primary-600"></i>
                    </span>
                    事件信息卡
                  </h2>
                  <dl className="space-y-4 text-sm">
                    <div>
                      <dt className="text-xs text-foreground-500 mb-1">核心人物</dt>
                      <dd className="text-foreground-900">{item.people.join(" · ")}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-foreground-500 mb-1">冲突点</dt>
                      <dd className="text-foreground-900 leading-relaxed">{item.conflict}</dd>
                    </div>
                    <div className="flex items-center gap-6 pt-2 border-t border-background-200">
                      <div>
                        <dt className="text-xs text-foreground-500 mb-1">讨论热度</dt>
                        <dd className="text-primary-600 flex items-center gap-1">
                          <i className="ri-fire-line"></i>
                          {formatHeat(item.heat)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-foreground-500 mb-1">收录日期</dt>
                        <dd className="text-foreground-900">{item.date}</dd>
                      </div>
                    </div>
                  </dl>
                </div>

                <div className="bg-secondary-100 border border-secondary-200 rounded-lg p-5">
                  <h2 className="font-heading font-bold text-sm text-foreground-950 mb-2">内容说明</h2>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    本条为网友投稿与公开讨论的整理汇总，涉及人物均为化名。
                    我们只还原公开信息的时间顺序，不对事实作裁定，也不建议作为任何判断依据。
                  </p>
                </div>

                <div className="bg-primary-900 rounded-lg p-5">
                  <h2 className="font-heading font-bold text-base text-background-50 mb-2">想继续看同一类？</h2>
                  <p className="text-sm text-primary-200 leading-relaxed mb-4">
                    该分类下还有更多条目，都是同样的整理方式。
                  </p>
                  <Link
                    to={`/rankings?category=${encodeURIComponent(item.category)}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
                  >
                    看全部 {item.category}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-14">
            <div className="flex items-end justify-between gap-4 mb-6">
              <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-950">相关推荐</h2>
              <Link
                to="/rankings"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
              >
                全部条目
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => (
                <GossipCard key={r.id} item={r} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}